import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import TimePicker from './TimePicker';
import { TIME_FORMAT , CREATE, EDIT} from '../constants';

const TimeRangePicker = ({ endLimit, startLimit, index, pickers, setTimePicker, availableSlots, acceptableBookingTime, minimumBookingPeriod=1, timeRangeType=CREATE, disable=false, useAntd=false, preferUrlTimes=false }) => {
  // Set step based on acceptableBookingTime: 30 minutes if true, 60 minutes if false
  const step = (acceptableBookingTime || minimumBookingPeriod < 1 || (minimumBookingPeriod % 1 !== 0)) ? 30 : 60;
  
  // Helper function to calculate end time based on minimum booking period
  const calculateEndTime = (startTime) => {
    const minBookingMinutes = minimumBookingPeriod * 60;
    return moment(startTime, TIME_FORMAT).add(minBookingMinutes, 'minutes').format(TIME_FORMAT);
  };
  
  // Use existing beginTime from pickers if available, otherwise calculate default
  const initialBeginTime = pickers[index]?.beginTime || 
    (pickers.length > 1 && pickers[index - 1]?.endTime
      ? moment(pickers[index - 1].endTime, TIME_FORMAT).add(1, 'hour').format(TIME_FORMAT)
      : '7:00 am');
  
  const [beginTime, setBeginTime] = useState(initialBeginTime);
  const [endTime, setEndTime] = useState(
    pickers[index]?.endTime || calculateEndTime(initialBeginTime)
  );

  useEffect(() => {
    const time = { beginTime, endTime };
    handleTimeValueChange(index, time);
  }, [beginTime, endTime]);

  // Keep local state in sync when the parent pre-filled times from URL params.
  useEffect(() => {
    if (!preferUrlTimes) {
      return;
    }
    const pickerBegin = pickers[index]?.beginTime;
    const pickerEnd = pickers[index]?.endTime;
    if (pickerBegin && pickerEnd) {
      setBeginTime(pickerBegin);
      setEndTime(pickerEnd);
    }
  }, [preferUrlTimes, pickers[index]?.beginTime, pickers[index]?.endTime]);

  // Apply the first available slot only when availability arrives — not when the user edits times.
  useEffect(() => {
    if (preferUrlTimes) {
      return;
    }

    if (availableSlots && availableSlots.length > 0 && timeRangeType !== EDIT) {
      const [initialSlot] = availableSlots;
      const startTime = moment(initialSlot.startTime, TIME_FORMAT).format(TIME_FORMAT);
      handleTimeChange(startTime, true);
    } else if (availableSlots && availableSlots.length > 0) {
      const startTime = moment(startLimit, TIME_FORMAT).format(TIME_FORMAT);
      handleTimeChange(startTime, true);
    } else {
      setEndTime(calculateEndTime(beginTime));
    }
  }, [availableSlots, minimumBookingPeriod, preferUrlTimes, timeRangeType, startLimit]);

  const handleTimeValue = picker => {
    const filteredList = pickers
      .filter((pick, index) => index !== picker)
      .map((timePicker, index) => {
        return timePicker;
      });
    setTimePicker(filteredList);
  };

  const handleTimeValueChange = (timeIndex, time) => {
    const filteredPickers = pickers.map((other, index) => {
      if (index === timeIndex) {
        return time;
      } else {
        return other;
      }
    });
    setTimePicker(filteredPickers);
  };

  const handleTimeChange = (value, forceChange) => {
    setBeginTime(value);
    let pickerList = pickers.slice();
    pickerList[index].beginTime = value;
    if(moment(pickerList[index].beginTime, TIME_FORMAT).isSameOrAfter(moment(pickerList[index].endTime, TIME_FORMAT)) || forceChange){
      if(timeRangeType !== EDIT) {
        handleEndTimeChange(handleTimeFormatting(value));
      }
    }
    setTimePicker(pickerList);
  };



  const handleEndTimeChange = value => {
    setEndTime(value);
    let pickerList = pickers.slice();
    pickerList[index].endTime = value;
    setTimePicker(pickerList);
  };

  const handleTimeFormatting = (time) =>{
    // Calculate minimum booking duration in minutes based on minimumBookingPeriod (in hours)
    // minimumBookingPeriod can be 0.5 (30 min), 1 (60 min), 1.5 (90 min), etc.
    return calculateEndTime(time || moment().format(TIME_FORMAT));
  }

  return (
    <div className="start-end-wrapper">
      <div className="start-end-inner-wrapper">
        {useAntd ? (
          <>
            <div className="crm-form-col crm-form-col--half">
              <TimePicker
                label={'Start Time'}
                value={pickers[index].beginTime}
                placeholder={'Starts'}
                beginLimit="12:00 am"
                onChange={value => handleTimeChange(value, true)}
                endLimit={availableSlots?.endTime||'11:00 pm'}
                availableSlots={availableSlots}
                acceptableBookingTime={acceptableBookingTime}
                step={step}
                disable={disable}
                minimumBookingPeriod={minimumBookingPeriod}
                useAntd={useAntd}
              />
            </div>
            <div className="crm-form-col crm-form-col--half">
              <TimePicker
                label={'End Time'}
                placeholder={'Ends'}
                value={pickers[index].endTime}
                beginLimit={handleTimeFormatting(beginTime)}
                onChange={value => handleEndTimeChange(value)}
                endLimit={availableSlots?.endTime||'11:00 pm'}
                availableSlots={availableSlots}
                acceptableBookingTime={acceptableBookingTime}
                startTime={pickers[index].beginTime}
                step={step}
                disable={disable}
                minimumBookingPeriod={minimumBookingPeriod}
                useAntd={useAntd}
              />
            </div>
          </>
        ) : (
          <>
            <TimePicker
              label={'Start Time'}
              value={pickers[index].beginTime}
              placeholder={'Starts'}
              beginLimit="12:00 am"
              onChange={value => handleTimeChange(value, true)}
              endLimit={availableSlots?.endTime||'11:00 pm'}
              availableSlots={availableSlots}
              acceptableBookingTime={acceptableBookingTime}
              step={step}
              disable={disable}
              minimumBookingPeriod={minimumBookingPeriod}
            />
            <TimePicker
              label={'End Time'}
              placeholder={'Ends'}
              value={pickers[index].endTime}
              beginLimit={handleTimeFormatting(beginTime)}
              onChange={value => handleEndTimeChange(value)}
              endLimit={availableSlots?.endTime||'11:00 pm'}
              availableSlots={availableSlots}
              acceptableBookingTime={acceptableBookingTime}
              startTime={pickers[index].beginTime}
              step={step}
              disable={disable}
              minimumBookingPeriod={minimumBookingPeriod}
            />
          </>
        )}
      </div>
    </div>
  );
};

TimeRangePicker.propTypes = {
  endLimit: PropTypes.string,
  pickers: PropTypes.array,
  setTimePicker: PropTypes.func,
  preferUrlTimes: PropTypes.bool
};

export default TimeRangePicker;
